import { ConsoleUserRoleEnum, UserRoleEnum } from '@api/enum';
import { IBoxStorageFile } from '@api/models';
import DocFileIcon from '@components/DocFileIcon/DocFileIcon';
import { Button, Table } from '@hyperflake/react-ui-library';
import { CopyIcon, DeleteIcon, DownloadIcon } from '@icon/index';
import { Popover } from '@library/popover';
import { Tooltip } from '@library/tooltip';
import { TRootState } from '@store/index';
import { getFileExtension } from '@utils/file-utils';

import moment from 'moment';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

interface DocumentListProps {
    isLoading: boolean;
    documents: IBoxStorageFile[];
    onDelete: (fileId: string) => void;
    deletingFileId: string | null;
}

const DocumentList = ({ isLoading, documents, onDelete, deletingFileId }: DocumentListProps) => {
    const [openPopoverFileId, setOpenPopoverFileId] = useState<string | null>(null);
    const { profile } = useSelector((state: TRootState) => state);
    const handleDelete = (row: IBoxStorageFile) => {
        onDelete(row._id); // delegate to parent
    };
    const handleDownload = (row: IBoxStorageFile) => {
        const link = document.createElement('a');
        link.href = row.url;
        link.download = row.filename || 'download'; // optional: custom filename
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleCopy = (url: string) => {
        navigator.clipboard.writeText(url);

        toast.success('Copied Successfully.');
    };
    return (
        <Table
            className="mt-6"
            borderStyle="divider"
            hover={false}
            isLoading={isLoading}
            data={documents}
            columns={[
                { header: 'File Name' },
                { header: 'Storage' },
                { header: 'Category' },

                // { header: 'File Size (KB)' },
                { header: 'Uploaded By' },
                { header: 'Created On' },
                { header: 'Actions' },
            ]}
            renderRow={(row) => (
                <Table.Row key={row._id}>
                    <Table.Cell className="flex gap-2 items-center">
                        <DocFileIcon className="w-7" extension={getFileExtension(row.filename)} />
                        {row.filename}
                    </Table.Cell>
                    <Table.Cell>{row.storage}</Table.Cell>
                    <Table.Cell>{row.category}</Table.Cell>

                    {/* <Table.Cell>{(row.fileSize / 1024).toFixed(2)}</Table.Cell> */}

                    <Table.Cell>{row.uploadedBy.name}</Table.Cell>

                    <Table.Cell>{moment(row.createdAt).format('DD MMM YYYY, h:mm A')}</Table.Cell>

                    <Table.Cell>
                        <div className="flex gap-6 items-center">
                            <Tooltip placement="top" content={'Copy url'}>
                                <button onClick={() => handleCopy(row.url)}>
                                    <CopyIcon width={18} height={18} />
                                </button>
                            </Tooltip>
                            <Tooltip placement="top" content={'Download'}>
                                <button onClick={() => handleDownload(row)} className="mb-2">
                                    <DownloadIcon width={18} height={18} />
                                </button>
                            </Tooltip>

                            <Popover
                                placement="left"
                                contentClassName="px-6 py-5 rounded-2xl shadow-lg w-[380px]"
                                open={openPopoverFileId === row._id}
                                setOpen={(open) => setOpenPopoverFileId(open ? row._id : null)}
                                content={
                                    <div>
                                        <p className="text-sm text-grayscale-500">
                                            Are you sure you want to remove this file?
                                        </p>
                                        <div className="flex gap-3 mt-4">
                                            <Button
                                                size="xs"
                                                color="primary"
                                                onClick={() => {
                                                    handleDelete(row);
                                                    setOpenPopoverFileId(null);
                                                }}
                                                disabled={deletingFileId === row._id}
                                            >
                                                {deletingFileId === row._id ? 'Deleting...' : 'Delete'}
                                            </Button>
                                            <Button
                                                size="xs"
                                                variant="shaded"
                                                onClick={() => setOpenPopoverFileId(null)}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                }
                            >
                                <Tooltip
                                    placement="top"
                                    content={
                                        profile.info.role !== ConsoleUserRoleEnum.ADMIN &&
                                        profile.info._id !== row.uploadedBy._id
                                            ? 'Only the uploader or an admin can delete this file.'
                                            : 'Delete'
                                    }
                                >
                                    <button
                                        onClick={() => setOpenPopoverFileId(row._id)}
                                        disabled={
                                            (profile.info.role !== ConsoleUserRoleEnum.ADMIN &&
                                                profile.info._id !== row.uploadedBy._id) ||
                                            deletingFileId === row._id
                                        }
                                    >
                                        <DeleteIcon width={18} height={18} />
                                    </button>
                                </Tooltip>
                            </Popover>
                        </div>
                    </Table.Cell>
                </Table.Row>
            )}
        />
    );
};

export default DocumentList;
